import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import htm from 'htm';

import { IconClose, IconReset, IconSend, IconMapPin, IconShare, IconAiChat } from './Icons.js';
import { AnimatedMessage } from './AnimatedMessage.js';
import { resetChatSession, apiShareSnapshot, apiGetSnapshot } from '../api.js';
import { getPanel } from '../panels/registry.js';
import { ShareModal } from './ShareModal.js';
import '../panels/map/index.js';
import '../panels/blocks/index.js';
import '../panels/tips/index.js';

import { resolveSuggestedButtons } from '../utils/suggestions.js';
import { useChatHistory } from '../hooks/useChatHistory.js';
import { useAutoScroll } from '../hooks/useAutoScroll.js';
import { usePanels } from '../hooks/usePanels.js';
import { useChatApi } from '../hooks/useChatApi.js';
import { useTipsApi } from '../hooks/useTipsApi.js';

const html = htm.bind(h);

/**
 * ChatBody component manages conversation state, user message dispatch,
 * automated scrolling behavior, suggested chip clicks, and leaflet map drawer interactions.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.config - The global widget configuration options.
 * @param {Function} props.t - The translation dictionary resolver function.
 * @param {string} [props.initialPrompt] - Initial seed query if opened via quick card.
 * @param {Function} props.onClose - Trigger callback when the drawer closing button is clicked.
 * @param {Function} props.onReady - Registration handler to expose send/prefill API to Web Component wrapper.
 * @param {ShadowRoot} props.shadowRoot - Root shadow DOM reference to inject leaflet styles.
 */
export function ChatBody({ config, t, initialPrompt, onClose, onReady, shadowRoot, sharedToken }) {
  const client = config?.client;
  const label = client ? client : t('header_suffix');

  const [input, setInput] = useState('');
  const [shareUrl, setShareUrl] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [sharingId, setSharingId] = useState(null);

  const [messages, setMessages] = useChatHistory(sharedToken);
  
  const {
    isPanelOpen,
    activePanel,
    isPanelOpenRef,
    openPanel,
    closePanel,
    panelInstanceRef,
    overlayMapData,
    setOverlayMapData
  } = usePanels(sharedToken, config);

  const inputRef = useRef(null);
  const panelContainerRef = useRef(null);
  const overlayMapContainerRef = useRef(null);
  const overlayMapInstanceRef = useRef(null);
  const sendRef = useRef(null);

  const {
    setTranscriptRef,
    scrollToBottom,
    stickToBottomRef,
    shouldForceFinalScrollRef,
    ignoreScrollEventsRef
  } = useAutoScroll(config?.client, sendRef);

  const {
    busy,
    setBusy,
    cancelRef,
    getStatusText,
    send,
    retry
  } = useChatApi({
    messages,
    setMessages,
    input,
    setInput,
    inputRef,
    sharedToken,
    config,
    stickToBottomRef,
    shouldForceFinalScrollRef,
    ignoreScrollEventsRef,
    isPanelOpenRef,
    setActivePanel: (p) => openPanel(p, p.messageId)
  });

  sendRef.current = send;

  const { loadTips, abortAllTips } = useTipsApi({ messages, setMessages });
  const loadTipsRef = useRef(loadTips);
  loadTipsRef.current = loadTips;

  // The message whose panel is currently shown. For the tips panel the live data
  // is the message's `tips` state (streamed independently of the main chat).
  const activePanelMessage = messages.find((m) => m.id === activePanel?.messageId);
  const activeTipsData = activePanel?.type === 'tips' ? (activePanelMessage?.tips || null) : null;

  useEffect(() => {
    onReady({
      send,
      prefill: (text) => {
        setInput(text);
        inputRef.current?.focus();
      },
      abort: () => {
        if (cancelRef.current) {
          cancelRef.current();
          cancelRef.current = null;
        }
      }
    });
  }, [send, onReady, cancelRef]);

  useEffect(() => {
    return () => {
      if (cancelRef.current) {
        cancelRef.current();
        cancelRef.current = null;
      }
    };
  }, [cancelRef]);

  useEffect(() => {
    if (sharedToken) return;
    if (initialPrompt) {
      const tId = setTimeout(() => send(initialPrompt), 200);
      return () => clearTimeout(tId);
    } else {
      const isTouch = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
      if (!isTouch) {
        const tId = setTimeout(() => inputRef.current?.focus(), 280);
        return () => clearTimeout(tId);
      }
    }
  }, [initialPrompt, send, sharedToken]);

  useEffect(() => {
    if (sharedToken) {
      setBusy(true);
      apiGetSnapshot(sharedToken)
        .then(snapshotData => {
          setMessages(snapshotData);
          setBusy(false);
        })
        .catch(err => {
          setMessages([{ id: 'error_msg', role: 'assistant', error: err.message, panels: [] }]);
          setBusy(false);
        });
    }
  }, [sharedToken, setMessages, setBusy]);

  const tRef = useRef(t);
  useEffect(() => { tRef.current = t; }, [t]);

  useEffect(() => {
    if (!isPanelOpen || !panelContainerRef.current || !activePanel) return;
    const plugin = getPanel(activePanel.type);
    if (!plugin) return;
    if (plugin.onOpen) plugin.onOpen(shadowRoot);

    // IMPORTANT: Clear the container before mounting a new plugin!
    // Some plugins (like Leaflet/Google Maps) have asynchronous destroy() methods.
    // If we don't clear the DOM manually, Preact (used by the Blocks panel) will see 
    // the leftover map elements and attempt to recycle/hijack them. Then, when the map's 
    // async destroy finishes a millisecond later, it deletes the hijacked element, 
    // resulting in a blank panel. DO NOT REMOVE THIS LINE!
    panelContainerRef.current.innerHTML = '';

    const mountData = activePanel.type === 'tips'
      ? (activeTipsData || activePanel.data)
      : activePanel.data;
    const mountMessageId = activePanel.messageId;

    const instance = plugin.mount(panelContainerRef.current, mountData, {
      t: tRef.current,
      config,
      label,
      onShowPinOnMap: (mapData) => setOverlayMapData(mapData),
      onRetry: () => loadTipsRef.current?.(mountMessageId),
      onAction: (text) => sendRef.current?.(text, 'followup')
    });
    panelInstanceRef.current = instance;

    return () => {
      if (panelInstanceRef.current) {
        panelInstanceRef.current.destroy();
        panelInstanceRef.current = null;
      }
    };
  }, [isPanelOpen, shadowRoot, activePanel?.type, config, panelInstanceRef, setOverlayMapData, activePanel]);

  useEffect(() => {
    if (isPanelOpen && panelInstanceRef.current && activePanel && activePanel.type !== 'tips') {
      panelInstanceRef.current.update(activePanel.data);
    }
  }, [activePanel, isPanelOpen, panelInstanceRef]);

  // Tips panel: push every streamed change of the message's tips state into the mounted view
  // without remounting it, so the typewriter animation keeps running smoothly.
  useEffect(() => {
    if (isPanelOpen && panelInstanceRef.current && activePanel?.type === 'tips' && activeTipsData) {
      panelInstanceRef.current.update(activeTipsData);
    }
  }, [activeTipsData, activePanel?.type, isPanelOpen, panelInstanceRef]);

  useEffect(() => {
    if (!overlayMapData || !overlayMapContainerRef.current) {
      if (overlayMapInstanceRef.current) {
        overlayMapInstanceRef.current.destroy();
        overlayMapInstanceRef.current = null;
      }
      return;
    }
    const mapPlugin = getPanel('map');
    if (!mapPlugin) return;
    if (mapPlugin.onOpen) mapPlugin.onOpen(shadowRoot);

    const instance = mapPlugin.mount(overlayMapContainerRef.current, overlayMapData, {
      t: tRef.current,
      config
    });
    overlayMapInstanceRef.current = instance;

    return () => {
      if (overlayMapInstanceRef.current) {
        overlayMapInstanceRef.current.destroy();
        overlayMapInstanceRef.current = null;
      }
    };
  }, [overlayMapData, shadowRoot, config]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
    if (e.key === 'Escape') {
      if (overlayMapData) setOverlayMapData(null);
      else if (isPanelOpen) closePanel();
      else onClose();
    }
  };

  const autosize = (e) => {
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 180) + 'px';
    setInput(el.value);
  };

  const reset = () => {
    if (cancelRef.current) cancelRef.current();
    cancelRef.current = null;
    abortAllTips();
    setMessages([]);
    setBusy(false);
    inputRef.current?.focus();
    closePanel();
    resetChatSession();
  };

  const handleShare = async (assistantMessage) => {
    if (busy || sharingId) return;
    setSharingId(assistantMessage.id);
    setShareCopied(false);
    const userMessageIndex = messages.findIndex(m => m.id === assistantMessage.id) - 1;
    const userMessage = messages[userMessageIndex];
    try {
      const token = await apiShareSnapshot([userMessage, assistantMessage]);
      const url = `${window.location.origin}${window.location.pathname}#tak=${token}`;
      setShareUrl(url);
    } catch (error) {
      console.error('Failed to share:', error);
    } finally {
      setSharingId(null);
    }
  };

  const copyShareUrl = () => {
    if (!shareUrl) return;
    try {
      const textarea = document.createElement('textarea');
      textarea.value = shareUrl;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2500);
    } catch (e) {
      console.error('Clipboard copy failed:', e);
    }
  };

  const hasConversation = messages.length > 0;
  // The tips panel renders its own chat-style skeleton, so the blur overlay is only for map/blocks.
  const isPanelLoading = activePanel?.type !== 'tips' && (busy || activePanelMessage?.streaming);

  const tipsPlugin = getPanel('tips');
  const tipsEnabled = !!tipsPlugin && (!tipsPlugin.isEnabled || tipsPlugin.isEnabled(config));

  /**
   * Renders the "local tips available" badge for an assistant message when the
   * Query Responder stream flagged `has_local_expert_tip` and delivered a `message_id`.
   */
  const renderTipsBadge = (m) => {
    if (!tipsEnabled || m.role !== 'assistant' || m.pending || m.streaming) return '';
    const tips = m.tips;
    if (!tips) return '';
    const isActive = isPanelOpen && activePanel?.messageId === m.id && activePanel?.type === 'tips';
    const hasContent = !!tips.content;
    if (!hasContent) {
      // Shared snapshots have no live chat session: only already-loaded tips can be shown.
      if (sharedToken) return '';
      if (!tips.available || !tips.messageId) return '';
      // The API ended without tips: hide the badge once the panel is closed.
      if (tips.empty && !isActive) return '';
    }
    const isLoading = tips.pending || tips.streaming;
    return html`
      <button
        type="button"
        class="mta-map-badge"
        aria-pressed=${isActive}
        aria-label=${isActive ? t('tips_close') : t('tips_open')}
        onClick=${() => {
          if (isActive) {
            closePanel();
            return;
          }
          openPanel({ type: 'tips', data: tips }, m.id);
          if (!sharedToken && !hasContent && !isLoading && !tips.error && !tips.empty) {
            loadTips(m.id);
          }
        }}
      >
        ${isLoading
          ? html`<span class="mta-map-syncing-spinner mta-tips-badge-spinner" aria-hidden="true"></span>`
          : html`<span class="mta-map-pulse" aria-hidden="true"><${tipsPlugin.icon} /></span>`}
        <span>${tipsPlugin.label(t)}</span>
      </button>
    `;
  };

  const plugin = activePanel ? getPanel(activePanel.type) : null;
  const activePanelIcon = plugin?.icon;
  const activePanelLabel = plugin?.label ? plugin.label(t) : '';
  const loadingText = activePanel?.type === 'map' 
    ? (t('map_loading') || 'Loading map…') 
    : (t('loading') || 'Loading…');

  const suggestedButtons = resolveSuggestedButtons(config, [
    { label: t('suggested_attractions'), query: t('suggested_attractions') },
    { label: t('suggested_dining'), query: t('suggested_dining') }
  ]);

  return html`
    <div class=${`mta-layout ${isPanelOpen ? 'is-map-open' : ''}`}>
      <div class="mta-chat">
        <header class="mta-header">
          <div class="mta-header-title">
            <span class="mta-status-dot" aria-hidden="true"></span>
            <span class="mta-sr-only">${t('status_online')}.</span>
            <span>${label}</span>
          </div>
          <div role="toolbar" aria-label=${label} style="display:flex;gap:2px;">
            ${hasConversation && !sharedToken && html`
              <button class="mta-icon-btn" type="button" title=${t('new_chat')} aria-label=${t('new_chat')} onClick=${reset}>
                <${IconReset} />
              </button>
            `}
            <button class="mta-icon-btn" type="button" title=${t('close')} aria-label=${t('close')} onClick=${onClose}>
              <${IconClose} />
            </button>
          </div>
        </header>

        ${hasConversation
      ? html`
              <div class="mta-transcript" role="log" aria-live="polite" aria-relevant="additions" aria-atomic="false" ref=${setTranscriptRef}>
                ${messages.map((m) => {
        return html`
                    <div class="mta-turn" key=${m.id}>
                      <div style="flex:1; width:100%;">
                        <div class=${`mta-bubble mta-bubble--${m.role}`}>
                          <div class="mta-turn-author" style="display:flex; align-items:center; margin-bottom:8px; font-weight:700; font-size:12px; color: ${m.role === 'user' ? 'inherit' : 'var(--mta-text-soft, #515151)'}; justify-content: ${m.role === 'user' ? 'flex-end' : 'flex-start'};">
                            ${m.role === 'user' ? t('author_user') : `✦ ${label}`}
                          </div>
                          ${(m.content || m.streaming) ? html`<${AnimatedMessage} message=${m} typingSpeed=${config?.typingSpeed} organicTyping=${config?.organicTyping} />` : ''}
                        </div>
                        ${m.error
            ? html`
                              <div class="mta-error-box" role="alert">
                                <span class="mta-error-message">${typeof m.error === 'string' ? m.error : t('error_connection')}</span>
                                ${!sharedToken && html`
                                  <button type="button" class="mta-retry-btn" onClick=${() => retry(m.id)}>
                                    ${t('retry')}
                                  </button>
                                `}
                              </div>
                            `
            : ''}
                        ${(m.pending || m.streaming) && m.role === 'assistant'
            ? html`
                              <div class="mta-skeleton" role="status" style="margin-top: ${m.content ? '8px' : '0'}; flex-direction: row; align-items: center; gap: 8px; padding: 4px 0;">
                                <span class="mta-sr-only">${t('skeleton_label')}</span>
                                <div class="mta-skeleton-line" aria-hidden="true" style="width: 28px; height: 8px; margin: 0; flex-shrink: 0;"></div>
                                ${getStatusText(m.status) ? html`<div style="font-size: 13px; color: var(--mta-text-muted); font-weight: 500; animation: mta-fade-in 0.3s ease;">${getStatusText(m.status)}</div>` : ''}
                              </div>
                            `
            : ''}
                        ${!m.pending && m.panels?.map((panel) => {
              const plugin = getPanel(panel.type);
              if (!plugin) return '';
              if (plugin.isEnabled && !plugin.isEnabled(config)) return '';
              const isActive = isPanelOpen && activePanel?.messageId === m.id && activePanel?.type === panel.type;
              return html`
                            <button
                              type="button"
                              class="mta-map-badge"
                              aria-pressed=${isActive}
                              aria-label=${isActive ? t('map_close') : t('map_open')}
                              onClick=${() => {
                  if (isActive) {
                    closePanel();
                  } else {
                    openPanel(panel, m.id);
                  }
                }}
                            >
                              <span class="mta-map-pulse" aria-hidden="true"><${plugin.icon} /></span>
                              <span>${plugin.label(t)}</span>
                            </button>
                          `;
            })}
                        ${renderTipsBadge(m)}
                        ${!m.pending && !m.streaming && m.role === 'assistant' && !sharedToken ? html`
                          <button
                            type="button"
                            class="mta-map-badge"
                            disabled=${sharingId === m.id}
                            onClick=${() => handleShare(m)}
                            style="${sharingId === m.id ? 'opacity:0.7; pointer-events:none;' : ''}"
                          >
                            ${sharingId === m.id ? html`
                              <span class="mta-map-syncing-spinner" style="width:12px; height:12px; border-width:2px;" aria-hidden="true"></span>
                            ` : html`
                              <span class="mta-map-pulse" aria-hidden="true" style="animation:none"><${IconShare} /></span>
                            `}
                            <span>${t('share')}</span>
                          </button>
                        ` : ''}
                      </div>
                    </div>
                  `;
      })}
              </div>
            `
      : html`
              <div class="mta-empty">
                <h2>${t('empty_title')}</h2>
                <p>${t('empty_subtitle')}</p>
                <div class="mta-suggestions" role="list">
                  ${resolveSuggestedButtons(config, [
        { label: t('suggestion_three_day'), query: t('suggestion_three_day') },
        { label: t('suggestion_things_to_do'), query: t('suggestion_things_to_do') },
        { label: t('suggestion_kid_friendly'), query: t('suggestion_kid_friendly') }
      ]).map((btn) => {
        return html`
                      <button type="button" class="mta-suggestion" role="listitem" onClick=${() => send(btn.query, 'suggested')}>${btn.label}</button>
                    `;
      })}
                </div>
              </div>
            `}

        ${sharedToken ? html`
          <div class="mta-read-only-banner" style="padding: 16px; text-align: center; border-top: 1px solid var(--mta-border); background: var(--mta-bg);">
            <p style="margin: 0 0 8px; font-size: 13px; color: var(--mta-text-soft);">
              ${t('shared_view_message')}
            </p>
            <button type="button" class="mta-suggestion" onClick=${() => {
              history.replaceState(null, null, ' ');
              window.location.reload();
            }}>
              ${t('start_new_chat')}
            </button>
          </div>
        ` : html`
          <div class="mta-composer">
            <div class="mta-composer-row">
              <div style="display:flex; align-items:center; justify-content:center; color:var(--mta-text-soft);">
                <${IconAiChat} />
              </div>
              <label class="mta-sr-only" for="mta-input">${t('placeholder_first')}</label>
              <textarea
                id="mta-input"
                class="mta-textarea"
                ref=${inputRef}
                placeholder=${hasConversation ? t('placeholder_reply') : t('placeholder_first')}
                value=${input}
                onInput=${autosize}
                onKeyDown=${onKeyDown}
                rows="1"
                aria-label=${hasConversation ? t('placeholder_reply') : t('placeholder_first')}
              ></textarea>
              <button
                class="mta-send-btn"
                type="button"
                onClick=${() => send()}
                disabled=${busy || !input.trim()}
                aria-label=${t('send')}
                title=${t('send')}
              >
                <${IconSend} />
              </button>
            </div>
            <div class="mta-composer-meta">
              <span><kbd>Enter</kbd> ${t('enter_to_send')} · <kbd>Shift</kbd>+<kbd>Enter</kbd> ${t('shift_enter_newline')}</span>
            </div>
          </div>
        `}
      </div>

      <aside class="mta-map-pane" aria-label="Panel" aria-hidden=${!isPanelOpen}>
        <div class="mta-panel-header">
          <div class="mta-panel-header-title">
            ${activePanelIcon && html`<span class="mta-panel-header-icon"><${activePanelIcon} /></span>`}
            <span class="mta-panel-header-label">${activePanelLabel}</span>
          </div>
          <button 
            type="button" 
            class="mta-panel-close-btn" 
            onClick=${closePanel} 
            aria-label=${t('close_panel') || 'Close panel'}
          >
            <${IconClose} />
          </button>
        </div>
        <div class="mta-panel-content">
          <div ref=${panelContainerRef} style="position:absolute;inset:0;"></div>
          ${isPanelLoading ? html`
            <div class="mta-map-streaming-overlay">
              <div class="mta-map-loading-card">
                <div class="mta-map-syncing-spinner"></div>
                <span>${loadingText}</span>
              </div>
            </div>
          ` : ''}
        </div>

        <div class=${`mta-panel-overlay ${overlayMapData ? 'is-active' : ''}`}>
          <div class="mta-panel-header">
            <div class="mta-panel-header-title">
              <span class="mta-panel-header-icon"><${IconMapPin} /></span>
              <span class="mta-panel-header-label">${t('show_on_map')}</span>
            </div>
            <button 
              type="button" 
              class="mta-panel-close-btn" 
              onClick=${() => setOverlayMapData(null)}
              aria-label=${t('map_close') || 'Close map'}
            >
              <${IconClose} />
            </button>
          </div>
          <div class="mta-overlay-map-content">
            <div ref=${overlayMapContainerRef} style="position:absolute;inset:0;"></div>
          </div>
        </div>
      </aside>

      <${ShareModal} 
        t=${t} 
        shareUrl=${shareUrl} 
        shareCopied=${shareCopied} 
        onClose=${() => setShareUrl(null)} 
        onCopy=${copyShareUrl} 
      />
    </div>
  `;
}
